<template>
  <a v-if="isLinkRenderable" class="lw-c-btn" :class="classes" :href="href" @click="handleClick">
    <i v-if="iconBefore" class="fa lw-c-btn__icon" :class="iconBefore" aria-hidden="true" />
    <slot />
  </a>
  <button
    v-else
    class="lw-c-btn"
    :class="classes"
    :type="type"
    :disabled="isButtonDisabled"
    :aria-busy="ariaBusy"
    :aria-disabled="ariaDisabled"
    @click="handleClick"
  >
    <i v-if="iconBefore" class="fa lw-c-btn__icon" :class="iconBefore" aria-hidden="true" />
    <span v-if="loading" class="lw-c-btn__spinner" aria-hidden="true" />
    <slot />
    <span v-if="loading" class="lw-c-btn__sr-only">{{ loadingLabel }}</span>
  </button>
</template>

<script>
/**
 * The button that deletes every `<span class="btn">` submit in the product
 * (Create Load, Update Load, Subhauler Report, Add Stop, the destructive
 * delete-stop control: audit_web.md 3.4). A `<span @click>` is never
 * focusable, so the element choice here is most of the point of the
 * component, not a styling detail.
 *
 * Element rule: a real `<button :type>` normally, a real `<a :href>` only
 * when href is set AND the control is neither disabled nor loading. There is
 * no honest way to disable an anchor (an `<a>` with no href is not a link,
 * and removing href mid-interaction breaks middle-click / open-in-new-tab
 * expectations), so a disabled or loading href falls back to a real
 * `<button disabled>` instead.
 */

const VARIANTS = ['primary', 'secondary', 'ghost', 'destructive', 'link'];
const SIZES = ['sm', 'md', 'lg'];
const TYPES = ['button', 'submit', 'reset'];

// Module-level, not component-level: the hierarchy rule is "one primary per
// view" (refresh_plan.md 1.1: "A view with two primaries fails its own
// test"), and a view is not something a single component instance can see
// from inside itself. This count is the closest approximation available: it
// counts every variant="primary" LwButton mounted anywhere in the app at
// once, which is honest about its own limit, not a precise per-route count.
let mountedPrimaryCount = 0;

export default {
  name: 'LwButton',
  props: {
    variant: {
      type: String,
      default: 'secondary',
      validator: value => VARIANTS.includes(value),
    },
    size: {
      type: String,
      default: 'md',
      validator: value => SIZES.includes(value),
    },
    // Applies to the <button> element only; an <a> has no submit/reset
    // semantics, so the anchor branch never reads this prop.
    type: {
      type: String,
      default: 'button',
      validator: value => TYPES.includes(value),
    },
    href: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    // A Font Awesome 4.7 class fragment, e.g. 'fa-plus'. FA 4.7 already ships
    // with the app; this prop does not add a dependency.
    iconBefore: {
      type: String,
      default: null,
    },
    loadingLabel: {
      type: String,
      default: 'Working',
    },
  },
  emits: ['click'],
  computed: {
    isLinkRenderable() {
      return this.href !== null && !this.disabled && !this.loading;
    },
    // href set, but the control cannot be an honest anchor right now: render
    // a real disabled <button> instead of a fake, un-hrefed <a>.
    hrefFallbackToDisabledButton() {
      return this.href !== null && (this.disabled || this.loading);
    },
    isButtonDisabled() {
      // A loading button with no href intentionally does NOT get the
      // disabled attribute here: a control that disables itself mid-action
      // drops keyboard focus to <body> and the user loses their place. The
      // href-fallback case above is the one exception, because there is no
      // partially-disabled anchor to fall back to instead.
      return this.disabled || this.hrefFallbackToDisabledButton;
    },
    ariaBusy() {
      return this.loading ? 'true' : null;
    },
    ariaDisabled() {
      return this.loading ? 'true' : null;
    },
    classes() {
      return [
        `lw-c-btn--${this.variant}`,
        `lw-c-btn--${this.size}`,
        {
          'lw-c-btn--full-width': this.fullWidth,
          'lw-c-btn--loading': this.loading,
        },
      ];
    },
    // Plucked for the primary-count warning, so the message names the
    // offending button rather than just the rule. Read from the rendered DOM
    // rather than calling `this.$slots.default()`: that call is only safe
    // (and only tracks dependencies) inside the component's own render pass,
    // and Vue 3 warns "Slot ... invoked outside of the render function" when
    // it is called from mounted(), which always runs after that pass has
    // finished. Only the element's own DIRECT TEXT children are read, which
    // is the exact DOM equivalent of the Vue 2 version's
    // `$slots.default.map(node => node.text)`: the default slot interpolates
    // as text directly under this element, while everything the component
    // renders around it is an element node — the Font Awesome glyph, the
    // spinner, and the loading state's visually-hidden progress label, which
    // is the one that would otherwise append "Working" to a loading button's
    // reported label.
    slotLabel() {
      return Array.from(this.$el.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE)
        .map(node => node.textContent)
        .join('')
        .trim();
    },
  },
  mounted() {
    if (this.variant !== 'primary') return;
    mountedPrimaryCount += 1;
    if (mountedPrimaryCount > 1) {
      // eslint-disable-next-line no-console -- deliberate warning surface for the one-primary-per-view rule (refresh_plan.md 1.1), not debug output
      console.warn(
        `LwButton: one primary per view, but ${mountedPrimaryCount} variant="primary" buttons are mounted. Offending label: "${this.slotLabel}".`
      );
    }
  },
  beforeUnmount() {
    if (this.variant !== 'primary') return;
    mountedPrimaryCount -= 1;
  },
  methods: {
    handleClick(event) {
      if (this.disabled || this.loading || this.hrefFallbackToDisabledButton) return;
      this.$emit('click', event);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--lw-space-2);
  border: 0;
  border-radius: var(--lw-radius-sm);
  cursor: pointer;
  background-color: transparent;
  // The button role token carries weight, size, line-height and family
  // together (refresh_plan.md 2.2: "so components stop re-typing it").
  // text-transform reads from its own role token too, deliberately not
  // hardcoded to uppercase: the prototype's plate-face caps treatment lives
  // in the token file's font/transform values, not in this component, so a
  // future token change (e.g. moving buttons onto the plate face) propagates
  // without touching this file.
  font: var(--lw-type-button);
  letter-spacing: var(--lw-type-button-tracking);
  text-transform: var(--lw-type-button-transform);
  padding: var(--lw-space-2) var(--lw-space-4);
  transition:
    background-color var(--lw-motion-fast) var(--lw-motion-ease),
    box-shadow var(--lw-motion-fast) var(--lw-motion-ease);

  @include lw-focus-visible;
}

.lw-c-btn--sm {
  padding: var(--lw-space-1) var(--lw-space-3);
}

.lw-c-btn--lg {
  padding: var(--lw-space-3) var(--lw-space-6);
  font: var(--lw-type-button-lg);
  letter-spacing: var(--lw-type-button-lg-tracking);
  text-transform: var(--lw-type-button-lg-transform);
}

.lw-c-btn--full-width {
  width: 100%;
}

.lw-c-btn--loading {
  cursor: progress;
}

// ===== Variants =====
// Amber is rationed to about one element per screen (refresh_plan.md 8), so
// primary is the only amber-ground thing this component ever renders.
.lw-c-btn--primary {
  background-color: var(--lw-amber-500);
  // Amber is never a ground for white text; the token comment on
  // --lw-fg-on-accent says so.
  color: var(--lw-fg-on-accent);
  // The prototype's inset bottom shade (direction-1.html .btn--primary,
  // ~line 238) used a translucent black shadow, a hardcoded colour function
  // hard rule 2 forbids. A solid navy-950 hairline at the same thickness
  // reads as the same pressed-plate shade while staying a plain token read.
  box-shadow: inset 0 -2px 0 var(--lw-navy-950);
}

.lw-c-btn--secondary {
  background-color: var(--lw-bg-inset);
  color: var(--lw-navy-800);
  box-shadow: inset 0 0 0 1px var(--lw-line-hairline);
}

.lw-c-btn--ghost {
  background-color: transparent;
  color: var(--lw-fg-secondary);
  box-shadow: inset 0 0 0 1px var(--lw-line-strong);
}

.lw-c-btn--destructive {
  // Reserved for actions that destroy or cancel (contract).
  background-color: var(--lw-reg-600);
  color: var(--lw-fg-inverse);
}

.lw-c-btn--link {
  background-color: transparent;
  color: var(--lw-navy-700);
  text-decoration: underline;
  border-radius: 0;
  // Overrides the size paddings above: a link never has button padding, at
  // any size, which is why this rule is ordered after the size modifiers.
  padding: 0;
}

// The real disabled attribute only ever lands on a <button> (an <a> the
// component renders is, by construction, never disabled). Colour it with
// --lw-fg-disabled on --lw-bg-inset with a hairline edge rather than
// opacity: opacity over a coloured fill is exactly how the driver app
// arrived at a 2.5:1 disabled contrast (audit_mobile.md 3.10, plan paper cut
// 14), and --lw-fg-disabled is tuned specifically so this state clears the
// contrast floor. Ordered last among the colour rules so it wins over every
// variant it can land on.
.lw-c-btn:disabled {
  background-color: var(--lw-bg-inset);
  color: var(--lw-fg-disabled);
  box-shadow: inset 0 0 0 1px var(--lw-line-hairline);
  cursor: not-allowed;
}

// ===== Loading spinner =====
.lw-c-btn__icon {
  // Font Awesome glyphs inherit size from the surrounding text; nothing to
  // set here beyond spacing, which the parent's flex gap already provides.
  line-height: 1;
}

.lw-c-btn__spinner {
  // Raw px: a small ring glyph has no token in the scale, the same way a
  // hairline or a clip-path corner does not (hard rule 2's stated
  // exception). Sized in em so it still tracks the button's own type scale
  // across sm/md/lg instead of needing a size per variant.
  width: 0.85em;
  height: 0.85em;
  border-radius: var(--lw-radius-pill);
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: lw-c-btn-spin var(--lw-motion-slow) linear infinite;
}

@keyframes lw-c-btn-spin {
  to {
    transform: rotate(360deg);
  }
}

// Standard visually-hidden clip technique: off-flow, zero-area, but still
// read by a screen reader. No token covers this shape, it is boilerplate
// geometry, not a stylistic choice.
.lw-c-btn__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
